import type { MasterProgram, ProgramLifecycle, ProgramStatus } from '../../../types/programs';
import { namesOf } from './programNaming';

/**
 * CORE-1100 — тексты и решение диалога удаления, отдельно от рендера.
 *
 * Здесь они потому, что это самая ломкая часть удаления: «удалить нельзя» и
 * «удалить навсегда» должны звучать одинаково на вебе и в нативе, а число
 * покупателей — склоняться. Вынесенные в чистые функции, обе вещи проверяются
 * тестом, а не разглядыванием двух экранов.
 */

/**
 * Продукт называется по-разному, и в подтверждении это слышно.
 *
 * CORE-993 — слова переехали в {@link programNaming}: то же различие понадобилось
 * шагу публикации, а два списка названий разошлись бы молча.
 */
export function programNoun(program: Pick<MasterProgram, 'productType'>): string {
  return namesOf(program).accusative;
}

export function programLabel(program: Pick<MasterProgram, 'title'>): string {
  return program.title?.trim() || 'Без названия';
}

/**
 * Удалять нельзя. Два источника, и оба обязательны: заранее спрошенный
 * `canHardDelete` и 409 на само удаление — между вопросом и нажатием программу
 * могли купить, и последнее слово всегда за сервером.
 */
export function isDeletionBlocked(
  lifecycle: ProgramLifecycle | null,
  serverRefused: boolean,
): boolean {
  return serverRefused || lifecycle?.canHardDelete === false;
}

/**
 * «Её купил один человек» и «её купили 12 человек» — разные фразы, и мастеру
 * важно именно число: оно объясняет запрет. Количество может быть и неизвестно
 * (сервер не ответил на lifecycle, а удаление отбил) — тогда честнее сказать про
 * сам факт покупок, чем печатать «0».
 */
export function buyersPhrase(count: number | null | undefined): string {
  if (count == null || count <= 0) return 'У неё есть покупки';

  const mod10 = count % 10;
  const mod100 = count % 100;
  const one = mod10 === 1 && mod100 !== 11;
  const few = mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14);

  const people = one ? 'человек' : few ? 'человека' : 'человек';
  return `Её ${one ? 'купил' : 'купили'} ${count} ${people}`;
}

export function deleteDialogTitle(program: MasterProgram, blocked: boolean): string {
  return blocked ? `Эту ${programNoun(program)} уже купили` : `Удалить ${programNoun(program)}?`;
}

/**
 * Что именно исчезнет — одна фраза на оба места, где мастер подтверждает
 * удаление: корзину на карточке и «Управление программой».
 *
 * Одна, потому что разойтись им нельзя: до CORE-1100 (заход 2) «Управление»
 * говорило «Черновик будет удалён безвозвратно» над ОПУБЛИКОВАННОЙ программой
 * и не называло её вовсе — мастер подтверждал удаление продукта, читая про
 * черновик. Статус меняет здесь не правило (удаляемость решает факт продажи,
 * а не статус), а перечень последствий: у черновика нет публичной страницы,
 * у опубликованного она есть, а архивное исчезает и из архива.
 */
export function deleteForeverSentence(
  program: Pick<MasterProgram, 'title' | 'productType'>,
  status: ProgramStatus | null | undefined,
): string {
  const name = `«${programLabel(program)}»`;
  const forever = 'Вернуть будет нечем.';

  switch (status) {
    case 'draft':
      return `Черновик ${name} и весь его состав пропадут навсегда. ${forever}`;
    case 'published':
      return `${name} и весь её состав пропадут навсегда — вместе со страницей `
        + `в вашем профиле и в публичной выдаче. ${forever}`;
    case 'archived':
      return `${name} и весь её состав пропадут навсегда — из архива тоже. ${forever}`;
    default:
      // Статус неизвестен (его не спросили или сервер промолчал). Врать про
      // черновик нельзя: без статуса верна только общая часть.
      return `${name} и весь её состав пропадут навсегда. ${forever}`;
  }
}

export function deleteDialogBody(
  program: MasterProgram,
  blocked: boolean,
  lifecycle: ProgramLifecycle | null,
): string {
  if (!blocked) {
    return deleteForeverSentence(program, program.status);
  }
  // Про доступ купивших сказано прямо: без этого «убрать в архив» читается как
  // «отобрать оплаченное», и мастер не нажмёт ни одну из кнопок.
  return `${buyersPhrase(lifecycle?.buyerCount)} — удалить нельзя: доступ и история покупок должны остаться. `
    + `Можно убрать «${programLabel(program)}» в архив: с витрины она пропадёт и продаваться перестанет, `
    + 'а те, кто уже купил, продолжат заниматься.';
}
